/**
 * Design-system kit — modern light SaaS primitives (Tailwind).
 * Shared building blocks for the redesigned Finance/Collections screens.
 * Kept dependency-light; icons are passed in by callers (lucide-react).
 */
import React from 'react';

export const cx = (...parts) => parts.filter(Boolean).join(' ');

// ── Spinner ──────────────────────────────────────────────────────────────────
export function Spinner({ className = '', size = 16 }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cx('inline-block animate-spin rounded-full border-2 border-current border-t-transparent align-[-2px]', className)}
      style={{ width: size, height: size }}
    />
  );
}

// ── Button ───────────────────────────────────────────────────────────────────
const BTN_VARIANTS = {
  primary: 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm',
  secondary: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
  ghost: 'bg-transparent text-gray-600 hover:bg-gray-100',
  danger: 'bg-rose-600 text-white hover:bg-rose-700 shadow-sm',
  // a step back, not a deletion: reopening, sending for recount
  warning: 'bg-amber-500 text-white hover:bg-amber-600 shadow-sm',
  subtle: 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100',
};
const BTN_SIZES = { sm: 'h-8 px-3 text-xs gap-1.5', md: 'h-9 px-4 text-sm gap-2', lg: 'h-10 px-5 text-sm gap-2' };

export function Button({ children, variant = 'secondary', size = 'md', loading = false, disabled, className = '', ...props }) {
  return (
    <button
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center rounded-lg font-medium transition-all duration-150',
        'active:scale-[.98] disabled:opacity-60 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
        BTN_VARIANTS[variant], BTN_SIZES[size], className,
      )}
      {...props}
    >
      {loading && <Spinner size={14} />}
      {children}
    </button>
  );
}

// ── Badge ────────────────────────────────────────────────────────────────────
const BADGE_TONES = {
  gray: 'bg-gray-100 text-gray-600 ring-gray-200',
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  amber: 'bg-amber-50 text-amber-700 ring-amber-200',
  orange: 'bg-orange-50 text-orange-700 ring-orange-200',
  red: 'bg-rose-50 text-rose-700 ring-rose-200',
  blue: 'bg-blue-50 text-blue-700 ring-blue-200',
  indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
};
export function Badge({ children, tone = 'gray', className = '' }) {
  return (
    <span className={cx('inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset whitespace-nowrap', BADGE_TONES[tone], className)}>
      {children}
    </span>
  );
}

// ── Card / Panel ─────────────────────────────────────────────────────────────
export function Card({ children, className = '', hover = false, ...props }) {
  return (
    <div
      {...props}
      className={cx('bg-white rounded-2xl border border-gray-200 shadow-sm', hover && 'transition-all duration-150 hover:shadow-md hover:-translate-y-0.5', className)}
    >
      {children}
    </div>
  );
}
// The title keeps a readable width; actions sit beside it while they fit and
// wrap under it, still to the right, when a card carries many of them.
export function CardHeader({ title, subtitle, actions, className = '' }) {
  return (
    <div className={cx('flex flex-wrap items-start justify-between gap-x-3 gap-y-2 px-5 py-4 border-b border-gray-100', className)}>
      <div className="min-w-0 flex-1 basis-64">
        {title && <h3 className="text-sm font-semibold text-gray-900">{title}</h3>}
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-2">{actions}</div>}
    </div>
  );
}
export function CardBody({ children, className = '' }) {
  return <div className={cx('p-5', className)}>{children}</div>;
}

// ── StatCard ─────────────────────────────────────────────────────────────────
const STAT_ACCENTS = {
  indigo: 'bg-indigo-50 text-indigo-600',
  green: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  red: 'bg-rose-50 text-rose-600',
  gray: 'bg-gray-100 text-gray-600',
};
export function StatCard({ label, value, sub, icon: Icon, accent = 'indigo', className = '' }) {
  return (
    <Card hover className={cx('p-5 flex items-start gap-4', className)}>
      {Icon && (
        <div className={cx('shrink-0 w-10 h-10 rounded-xl flex items-center justify-center', STAT_ACCENTS[accent])}>
          <Icon size={18} strokeWidth={2.2} />
        </div>
      )}
      <div className="min-w-0">
        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
        <div className="text-2xl font-semibold text-gray-900 mt-1 tabular-nums truncate">{value}</div>
        {sub != null && <div className="text-xs text-gray-500 mt-0.5 truncate">{sub}</div>}
      </div>
    </Card>
  );
}

// ── Segmented Tabs ───────────────────────────────────────────────────────────
export function Tabs({ items, value, onChange, className = '' }) {
  return (
    <div className={cx('inline-flex items-center gap-1 p-1 bg-gray-100 rounded-xl', className)}>
      {items.map((it) => {
        const active = it.key === value;
        return (
          <button
            key={it.key}
            onClick={() => onChange(it.key)}
            className={cx(
              'inline-flex items-center gap-2 px-3.5 h-8 rounded-lg text-sm font-medium transition-all duration-150',
              active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800',
            )}
          >
            {it.label}
            {it.count != null && (
              <span className={cx('text-xs px-1.5 rounded-full', active ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-200 text-gray-600')}>
                {it.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Skeleton ─────────────────────────────────────────────────────────────────
export function Skeleton({ className = '' }) {
  return <div className={cx('animate-pulse bg-gray-200 rounded-md', className)} />;
}

// ── EmptyState ───────────────────────────────────────────────────────────────
export function EmptyState({ icon: Icon, title, description, action, className = '' }) {
  return (
    <div className={cx('flex flex-col items-center justify-center text-center py-14 px-6', className)}>
      {Icon && (
        <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
          <Icon size={22} className="text-gray-400" />
        </div>
      )}
      <p className="text-sm font-medium text-gray-900">{title}</p>
      {description && <p className="text-sm text-gray-500 mt-1 max-w-sm">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ── PageHeader ───────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions, className = '' }) {
  return (
    <div className={cx('flex flex-wrap items-start justify-between gap-4 mb-6', className)}>
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

// ── PaginationBar ─────────────────────────────────────────────────────────────
export function PaginationBar({
  currentPage = 1,
  totalItems = 0,
  pageSize = 20,
  pageSizeOptions = [10, 20, 50, 100],
  onPageChange,
  onPageSizeChange,
  className = '',
}) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const pages = [];
  const maxButtons = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
  let endPage = startPage + maxButtons - 1;
  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - maxButtons + 1);
  }
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className={cx('flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-white border-t border-gray-200 text-xs text-gray-600 rounded-b-2xl', className)}>
      <div className="flex items-center gap-3">
        {onPageSizeChange && (
          <div className="flex items-center gap-1.5">
            <span className="text-gray-500">Items per page:</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-8 rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
            >
              {pageSizeOptions.map((sz) => (
                <option key={sz} value={sz}>{sz}</option>
              ))}
            </select>
          </div>
        )}
        <span>
          Showing <strong className="font-semibold text-gray-900">{startItem}</strong> to <strong className="font-semibold text-gray-900">{endItem}</strong> of <strong className="font-semibold text-gray-900">{totalItems}</strong> entries
        </span>
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={currentPage <= 1}
          onClick={() => onPageChange && onPageChange(currentPage - 1)}
          className="h-8 px-3 rounded-lg border border-gray-300 bg-white font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none transition"
        >
          Previous
        </button>

        {pages.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPageChange && onPageChange(p)}
            className={cx(
              'h-8 w-8 rounded-lg font-medium transition',
              p === currentPage
                ? 'bg-indigo-600 text-white font-semibold shadow-sm'
                : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            )}
          >
            {p}
          </button>
        ))}

        <button
          type="button"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange && onPageChange(currentPage + 1)}
          className="h-8 px-3 rounded-lg border border-gray-300 bg-white font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none transition"
        >
          Next
        </button>
      </div>
    </div>
  );
}

// ── Pager ────────────────────────────────────────────────────────────────────
// Long tables are read a screen at a time. `usePager` slices the rows and keeps
// the page in range when the list under it changes; <Pager> is the strip that
// says where you are and how many there are in total.
export function usePager(rows, initialSize = 25) {
  const [page, setPage] = React.useState(1);
  const [size, setSize] = React.useState(initialSize);
  const total = (rows || []).length;
  const pages = Math.max(1, Math.ceil(total / size));
  React.useEffect(() => { if (page > pages) setPage(1); }, [pages, page]);
  const from = (page - 1) * size;
  return { page, setPage, size, setSize, pages, total,
           shown: (rows || []).slice(from, from + size),
           first: total ? from + 1 : 0, last: Math.min(from + size, total) };
}

export function Pager({ pager, unit = 'rows', sizes = [25, 50, 100, 250], className = '' }) {
  const { page, setPage, size, setSize, pages, total, first, last } = pager;
  if (total <= Math.min(...sizes)) return null;
  const go = (n) => setPage(Math.min(pages, Math.max(1, n)));
  return (
    <div className={cx('flex flex-wrap items-center gap-2 pt-2 text-xs text-gray-500', className)}>
      <span>{first}–{last} of {total} {unit}</span>
      <span className="flex-1" />
      <label className="flex items-center gap-1">
        <span className="sr-only">Rows per page</span>
        <select value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }}
          aria-label="Rows per page" className="rounded-md border border-gray-300 px-1.5 py-1 bg-white">
          {sizes.map((n) => <option key={n} value={n}>{n} per page</option>)}
        </select>
      </label>
      <span className="flex items-center gap-1">
        <button type="button" onClick={() => go(page - 1)} disabled={page <= 1} aria-label="Previous page"
          className="rounded-md border border-gray-300 px-2 py-1 disabled:opacity-40 hover:bg-gray-50">‹</button>
        <span className="tabular-nums px-1">{page} / {pages}</span>
        <button type="button" onClick={() => go(page + 1)} disabled={page >= pages} aria-label="Next page"
          className="rounded-md border border-gray-300 px-2 py-1 disabled:opacity-40 hover:bg-gray-50">›</button>
      </span>
    </div>
  );
}
